import { useEffect, useState, useCallback } from "react";
import axios from "../../public Func/axiosAuth";
import {
  SearchBar,
  Button,
  Modal,
  LoadingSkeleton,
  Select,
  PriceBreakdownCell,
} from "../../components/ui";
import AppointmentDetailsModal from "./AppointmentDetailsModal";
import { getCairoDateString, getCairoDayRange } from "./appointmentTime";
import "./AppointmentsPage.css";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "scheduled", label: "Scheduled" },
  { value: "completed", label: "Completed" },
  { value: "canceled", label: "Canceled" },
];

const DATE_FILTER_OPTIONS = [
  { value: "all", label: "All dates" },
  { value: "today", label: "Today only" },
];

function AppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [page, setPage] = useState(1);

  const [modalAppointment, setModalAppointment] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [remindingId, setRemindingId] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [completeModalAppointment, setCompleteModalAppointment] =
    useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Row actions dropdown
  const [actionMenu, setActionMenu] = useState(null);

  // Pinned phone tooltip (shown on click, copies phone to clipboard)
  const [pinnedPhone, setPinnedPhone] = useState(null);
  const [copied, setCopied] = useState(false);

  // Notes editing
  const [notesModalAppointment, setNotesModalAppointment] = useState(null);
  const [notesText, setNotesText] = useState("");

  // Row details
  const [detailAppt, setDetailAppt] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!detailAppt) return;
    setNow(new Date());
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, [detailAppt]);

  useEffect(() => {
    if (!actionMenu) return undefined;
    const closeMenu = () => setActionMenu(null);
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeMenu();
    };
    document.addEventListener("mousedown", closeMenu);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", closeMenu);
    window.addEventListener("scroll", closeMenu, true);
    return () => {
      document.removeEventListener("mousedown", closeMenu);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", closeMenu);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [actionMenu]);

  // Close the menu when the list is refreshed (e.g. after an action).
  useEffect(() => {
    setActionMenu(null);
  }, [appointments]);

  const openDetailsModal = (appt) => setDetailAppt(appt);

  const closeDetailsModal = () => setDetailAppt(null);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (dateFilter === "today" && fromDate) {
        // "Today only" is resolved here: send the exact Cairo day bounds so the
        // backend's raw-instant comparison covers the whole day.
        const { fromDate: todayFrom, toDate: todayTo } =
          getCairoDayRange(fromDate);
        params.set("fromDate", todayFrom);
        params.set("toDate", todayTo);
      } else {
        if (fromDate) params.set("fromDate", fromDate);
        if (toDate) params.set("toDate", toDate);
      }
      if (searchParam.trim()) params.set("search", searchParam.trim());
      params.set("page", String(page));
      params.set("limit", "10");

      const res = await axios.get(
        process.env.REACT_APP_BACKEND_URL +
        "/admin/appointments?" +
        params.toString(),
      );
      const payload = res.data;
      if (payload && payload.data) {
        setAppointments(payload.data);
        setPagination(payload.pagination || null);
      } else {
        setAppointments([]);
        setPagination(null);
      }
    } catch (err) {
      console.error("Error fetching appointments:", err);
      setAppointments([]);
      setPagination(null);
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to load appointments.",
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFilter, fromDate, toDate, searchParam, page]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchParam(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value || "");
    setPage(1);
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value || "");
    setPage(1);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value || "");
    setPage(1);
  };

  // "Today only" pins both date inputs to today's Egypt calendar day; the
  // inputs are disabled while it is active so the two cannot drift apart.
  const handleDateFilterChange = (e) => {
    const isToday = e.target.value === "today";
    setDateFilter(isToday ? "today" : "all");
    setFromDate(isToday ? getCairoDateString() : "");
    setToDate(isToday ? getCairoDateString() : "");
    setPage(1);
  };

  const clearFilters = () => {
    setStatusFilter("");
    setDateFilter("all");
    setFromDate("");
    setToDate("");
    setSearchInput("");
    setSearchParam("");
    setPage(1);
  };

  const openMarkScheduledModal = (appointment) => {
    setModalAppointment(appointment);
    setReceiptFile(null);
    setMessage({ type: "", text: "" });
  };

  const closeModal = () => {
    setModalAppointment(null);
    setReceiptFile(null);
    setMessage({ type: "", text: "" });
  };

  const handleMarkScheduledSubmit = async () => {
    if (!modalAppointment) return;
    setSubmitting(true);
    setMessage({ type: "", text: "" });
    try {
      let receiptUrl = null;
      if (receiptFile) {
        const formData = new FormData();
        formData.append("file", receiptFile);
        const uploadRes = await axios.post(
          process.env.REACT_APP_BACKEND_URL + "/upload",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        receiptUrl =
          typeof uploadRes.data === "string"
            ? uploadRes.data
            : uploadRes.data?.link ||
            uploadRes.data?.file ||
            uploadRes.data?.url;
      }
      await axios.post(
        process.env.REACT_APP_BACKEND_URL +
        "/admin/appointments/mark-scheduled",
        {
          appointmentID: modalAppointment.id,
          ...(receiptUrl && { receiptUrl }),
        },
      );
      closeModal();
      fetchAppointments();
      setMessage({ type: "success", text: "Appointment marked as scheduled." });
    } catch (err) {
      console.error("Error marking appointment as scheduled:", err);
      setMessage({
        type: "error",
        text:
          err.response?.data?.message ||
          err.response?.data ||
          "Failed to update appointment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReminder = async (appointmentID) => {
    setRemindingId(appointmentID);
    setMessage({ type: "", text: "" });
    try {
      await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/admin/appointments/send-reminder",
        { appointmentID },
      );
      setMessage({
        type: "success",
        text: "Reminder sent to patient and doctor.",
      });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to send reminder.",
      });
    } finally {
      setRemindingId(null);
    }
  };

  const openCompleteModal = (appointment) => {
    setCompleteModalAppointment(appointment);
    setMessage({ type: "", text: "" });
  };

  const closeCompleteModal = () => {
    if (completingId) return;
    setCompleteModalAppointment(null);
  };

  const handleMarkComplete = async (appointmentID) => {
    setCompletingId(appointmentID);
    setMessage({ type: "", text: "" });
    try {
      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL +
        "/admin/appointments/mark-as-complete",
        { appointmentID },
      );
      setCompleteModalAppointment(null);
      fetchAppointments();
      setMessage({
        type: "success",
        text: res.data?.message || "Appointment marked as complete.",
      });
    } catch (err) {
      console.error("Error marking appointment as complete:", err);
      setMessage({
        type: "error",
        text:
          err.response?.data?.message ||
          err.response?.data ||
          "Failed to mark appointment as complete.",
      });
    } finally {
      setCompletingId(null);
    }
  };

  const getAppointmentActions = (appointment) => {
    const actions = [];

    if (appointment.status === "pending") {
      actions.push({
        id: "confirm-payment",
        label: "Confirm Payment",
        onClick: () => openMarkScheduledModal(appointment),
      });
    }

    if (appointment.status === "scheduled") {
      actions.push({
        id: "send-reminder",
        label:
          remindingId === appointment.id ? "Sending..." : "Send Reminder",
        disabled: remindingId === appointment.id,
        onClick: () => handleSendReminder(appointment.id),
      });
    }

    if (
      appointment.status === "scheduled" ||
      appointment.status === "missed"
    ) {
      actions.push({
        id: "mark-complete",
        label:
          completingId === appointment.id
            ? "Completing..."
            : "Mark as Complete",
        variant: "success",
        disabled: completingId === appointment.id,
        onClick: () => openCompleteModal(appointment),
      });
    }

    return actions;
  };

  const toggleActionMenu = (event, appointment) => {
    event.stopPropagation();
    if (actionMenu?.appointment.id === appointment.id) {
      setActionMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 190;
    const menuHeight = getAppointmentActions(appointment).length * 40 + 8;
    const openUp = rect.bottom + menuHeight + 8 > window.innerHeight;

    setActionMenu({
      appointment,
      left: Math.max(
        8,
        Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8),
      ),
      top: openUp
        ? Math.max(8, rect.top - menuHeight - 6)
        : rect.bottom + 6,
    });
  };

  const openNotesModal = (apt) => {
    setNotesText(apt.notes || "");
    setNotesModalAppointment(apt);
  };

  const handlePhonePin = async (e, person) => {
    e.stopPropagation();
    if (!person?.phoneNumber) return;
    if (pinnedPhone && pinnedPhone.phone === person.phoneNumber) {
      setPinnedPhone(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(person.phoneNumber);
      }
    } catch (err) {
      console.error("Clipboard copy failed:", err);
    }
    setCopied(true);
    setPinnedPhone({
      name: person.name,
      phone: person.phoneNumber,
      x: rect.left,
      y: rect.top - 12,
    });
    window.setTimeout(() => setCopied(false), 1600);
  };

  const closeNotesModal = () => {
    setNotesModalAppointment(null);
    setNotesText("");
  };

  const handleSaveNotes = async () => {
    if (!notesModalAppointment) return;
    try {
      await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/admin/appointments/notes",
        {
          appointmentID: notesModalAppointment.id,
          notes: notesText.trim() || null,
        },
      );
      closeNotesModal();
      fetchAppointments();
      setMessage({ type: "success", text: "Notes saved." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to save notes.",
      });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "\u2014";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "Africa/Cairo",
    });
  };

  const getRowClass = (apt) => {
    if (apt.status === "canceled") return "row-canceled";
    if (apt.status === "pending") return "row-pending";
    if (apt.status === "scheduled") return "row-scheduled";
    if (apt.status === "completed") return "row-completed";
    if (apt.status === "missed") return "row-missed";
    return "";
  };

  return (
    <div className="appointments-page">
      <div className="appointments-header">
        <div>
          <h1 className="page-title">Appointments</h1>
          <p className="page-subtitle">
            {loading
              ? "Loading..."
              : pagination
                ? `${pagination.total} total`
                : "Manage and verify appointments"}
          </p>
        </div>
      </div>

      <div className="appointments-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label className="filter-label">Status</label>
            <Select
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={handleStatusChange}
              className="filter-select"
            />
          </div>
          <div className="filter-group">
            <label className="filter-label" htmlFor="appointments-from-date">
              From date
            </label>
            <input
              id="appointments-from-date"
              type="date"
              className="filter-date-input"
              value={fromDate}
              onChange={handleFromDateChange}
              disabled={dateFilter === "today"}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label" htmlFor="appointments-to-date">
              To date
            </label>
            <input
              id="appointments-to-date"
              type="date"
              className="filter-date-input"
              value={toDate}
              onChange={handleToDateChange}
              disabled={dateFilter === "today"}
            />
          </div>
          <fieldset className="filter-radio-group">
            <legend className="filter-label">Dates</legend>
            {DATE_FILTER_OPTIONS.map((option) => (
              <label
                key={option.value}
                className="filter-radio"
                htmlFor={`appointment-date-filter-${option.value}`}
              >
                <input
                  type="radio"
                  id={`appointment-date-filter-${option.value}`}
                  name="appointment-date-filter"
                  value={option.value}
                  checked={dateFilter === option.value}
                  onChange={handleDateFilterChange}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </fieldset>
        </div>
        <div className="search-row">
          <SearchBar
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search by patient or doctor name..."
            size="md"
            fullWidth
            disabled={loading}
          />
        </div>
      </div>

      {message.text && (
        <div
          className={`appointments-message appointments-message-${message.type}`}
        >
          {message.text}
        </div>
      )}

      <div className="appointments-table-wrap">
        {loading ? (
          <div className="appointments-skeleton">
            <LoadingSkeleton width="100%" height="40px" />
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <LoadingSkeleton key={i} width="100%" height="56px" />
            ))}
          </div>
        ) : appointments.length === 0 ? (
          <div className="appointments-empty">
            <p>No appointments found.</p>
            {(statusFilter ||
              dateFilter === "today" ||
              fromDate ||
              toDate ||
              searchParam) && (
                <Button variant="secondary" onClick={clearFilters}>
                  Clear filters
                </Button>
              )}
          </div>
        ) : (
          <>
            <table className="appointments-table">
              <thead>
                <tr>
                  <th>Date &amp; time</th>
                  <th>Patient</th>
                  <th>Doctor</th>
                  <th>Price</th>
                  <th>Notes</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((apt) => (
                  <tr
                    key={apt.id}
                    className={getRowClass(apt)}
                    onClick={() => openDetailsModal(apt)}
                  >
                    <td>
                      {formatDate(apt.date)}
                      {apt.timeSlotInfo && (
                        <span className="slot-info">
                          {apt.timeSlotInfo.startTime} {"\u2013"}{" "}
                          {apt.timeSlotInfo.duration} min
                        </span>
                      )}
                    </td>
                    <td
                      className="person-cell"
                      title={apt.patient?.phoneNumber || undefined}
                      onClick={(e) => handlePhonePin(e, apt.patient)}
                    >
                      {apt.patient?.name ?? "\u2014"}
                    </td>
                    <td
                      className="person-cell"
                      title={apt.doctor?.phoneNumber || undefined}
                      onClick={(e) => handlePhonePin(e, apt.doctor)}
                    >
                      {apt.doctor?.name ?? "\u2014"}
                    </td>
                    <PriceBreakdownCell
                      price={apt.price}
                      systemCut={apt.systemCut}
                      voucherCode={apt.voucherCode}
                      discountType={apt.discountType}
                      discountValue={apt.discountValue}
                      discountAmountApplied={apt.discountAmountApplied}
                      doctorCut={apt.doctorCut}
                      finalPrice={apt.finalPrice}
                    />
                    <td className="notes-cell">
                      <span className="notes-text" title={apt.notes || ""}>
                        {apt.notes
                          ? apt.notes.length > 40
                            ? apt.notes.slice(0, 40) + "..."
                            : apt.notes
                          : "\u2014"}
                      </span>
                      <button
                        className="notes-edit-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openNotesModal(apt);
                        }}
                        title="Edit notes"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </td>
                    <td>
                      <div className="row-actions">
                        {getAppointmentActions(apt).length > 0 ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            className="row-actions-trigger"
                            aria-haspopup="menu"
                            aria-expanded={
                              actionMenu?.appointment.id === apt.id
                            }
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => toggleActionMenu(e, apt)}
                            rightIcon={
                              <svg
                                className="row-actions-caret"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            }
                          >
                            Actions
                          </Button>
                        ) : (
                          <span className="row-actions-empty">
                            {"\u2014"}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination && pagination.totalPages > 1 && (
              <div className="appointments-pagination">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!pagination.hasPreviousPage || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.totalPages} (
                  {pagination.total} total)
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!pagination.hasNextPage || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {actionMenu && (
        <div
          className="row-actions-menu"
          role="menu"
          style={{ left: actionMenu.left, top: actionMenu.top }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {getAppointmentActions(actionMenu.appointment).map((action) => (
            <button
              key={action.id}
              type="button"
              role="menuitem"
              className={[
                "row-actions-menu-item",
                action.variant === "success"
                  ? "row-actions-menu-item-success"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              disabled={action.disabled}
              onClick={() => {
                setActionMenu(null);
                action.onClick();
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      <Modal
        isOpen={!!modalAppointment}
        onClose={closeModal}
        title="Confirm Payment"
        size="md"
        footer={
          <div className="modal-footer-actions">
            <Button
              variant="secondary"
              onClick={closeModal}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleMarkScheduledSubmit}
              loading={submitting}
            >
              Mark as scheduled
            </Button>
          </div>
        }
      >
        {modalAppointment && (
          <div className="mark-scheduled-content">
            <p className="mark-scheduled-detail">
              Patient: <strong>{modalAppointment.patient?.name}</strong>{" "}
              {"\u00B7"} Doctor:{" "}
              <strong>{modalAppointment.doctor?.name}</strong>
            </p>
            <p className="mark-scheduled-detail">
              Date: {formatDate(modalAppointment.date)}
            </p>
            <div className="receipt-upload-group">
              <label className="filter-label">Receipt (optional)</label>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                className="receipt-file-input"
              />
              {receiptFile && (
                <span className="receipt-file-name">{receiptFile.name}</span>
              )}
            </div>
            {message.text && modalAppointment && (
              <div
                className={`appointments-message appointments-message-${message.type}`}
              >
                {message.text}
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!notesModalAppointment}
        onClose={closeNotesModal}
        title="Appointment Notes"
        size="md"
        footer={
          <div className="modal-footer-actions">
            <Button variant="secondary" onClick={closeNotesModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveNotes}>
              Save Notes
            </Button>
          </div>
        }
      >
        {notesModalAppointment && (
          <div className="notes-modal-content">
            <p className="notes-modal-detail">
              {notesModalAppointment.patient?.name} {"\u00B7"}{" "}
              {notesModalAppointment.doctor?.name}
            </p>
            <textarea
              className="notes-textarea"
              rows={5}
              placeholder="Add admin notes..."
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
            />
            {notesModalAppointment.review ? (
              <div className="review-section">
                <label className="review-section-label">Patient Review</label>
                <div className="review-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={`review-star ${star <= notesModalAppointment.review.stars ? "filled" : ""}`}
                    >
                      ★
                    </span>
                  ))}
                </div>
                {notesModalAppointment.review.text && (
                  <p className="review-text">
                    {notesModalAppointment.review.text}
                  </p>
                )}
              </div>
            ) : (
              <div className="review-section">
                <p className="review-empty">No patient review</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!completeModalAppointment}
        onClose={closeCompleteModal}
        title="Mark as Complete"
        size="md"
        footer={
          <div className="modal-footer-actions">
            <Button
              variant="secondary"
              onClick={closeCompleteModal}
              disabled={!!completingId}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={() => handleMarkComplete(completeModalAppointment.id)}
              loading={!!completingId}
            >
              Mark as complete
            </Button>
          </div>
        }
      >
        {completeModalAppointment && (
          <div className="complete-modal-content">
            <p className="complete-modal-detail">
              Patient: <strong>{completeModalAppointment.patient?.name}</strong>{" "}
              {"\u00B7"} Doctor:{" "}
              <strong>{completeModalAppointment.doctor?.name}</strong>
            </p>
            <p className="complete-modal-detail">
              Date: {formatDate(completeModalAppointment.date)}
            </p>
            <p className="complete-modal-detail">
              This will mark the appointment as completed and create a chatroom
              for the patient and doctor.
            </p>
            {message.text && (
              <div
                className={`appointments-message appointments-message-${message.type}`}
              >
                {message.text}
              </div>
            )}
          </div>
        )}
      </Modal>

      <AppointmentDetailsModal
        appointment={detailAppt}
        now={now}
        onClose={closeDetailsModal}
      />

      {pinnedPhone && (
        <div
          className="phone-pin-tooltip"
          style={{ left: pinnedPhone.x, top: pinnedPhone.y }}
          onClick={() => setPinnedPhone(null)}
        >
          <div className="phone-pin-name">{pinnedPhone.name}</div>
          <div className="phone-pin-number">
            {pinnedPhone.phone}
            {copied && <span className="phone-pin-copied"> · copied</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default AppointmentsPage;
